import { insetBy } from './geometry';
import { Matrix } from './matrix';
import { Rect } from './rect';

export type FrameLikeElement = HTMLIFrameElement | HTMLFrameElement;

/**
 * Return a list, including the provided window, of all the descendant <iframe> and <frame> elements.
 * Assumes all descendant frames are same-domain.
 */
export function getAllDescendantWindowsFromDoc(wnd: Window): (Window | null)[] {
  const _result = [];
  _result.push(wnd);
  if (!wnd.document) {
    return _result;
  }

  getAllChildFrames(wnd.document).forEach(childFrame => {
    const childFrames: FrameLikeElement[] = [];
    getAllDescendantFrames(childFrame as FrameLikeElement, childFrames);
    childFrames.forEach(frame => _result.push(frame.contentWindow));
  });

  return _result;
}

/**
 * Return a list including all <iframe> and <frame> elements in the privided
 * document. In other words, the list includes all immediate child frames of
 * the doc. Assumes all descendant frames are same-domain.
 */
export function getAllChildFrames(doc: Document): FrameLikeElement[] {
  const result: FrameLikeElement[] = [];
  if (!doc) {
    return result;
  }
  const children = doc.querySelectorAll('iframe,frame');
  result.push(...(children as NodeListOf<FrameLikeElement>));
  return result;
}

/**
 * Return a list including `frame` and all its descendant <iframe> and <frame> elements.
 * Assumes all descendant frames are same-domain.
 */
export function getAllDescendantFrames(
  frame: FrameLikeElement,
  _result: FrameLikeElement[] = [],
): FrameLikeElement[] {
  _result.push(frame);
  if (!frame.contentDocument) {
    return _result;
  }

  const frames = frame.contentDocument.querySelectorAll('iframe,frame');
  for (const childFrame of frames) {
    getAllDescendantFrames(childFrame as FrameLikeElement, _result);
  }

  return _result;
}

/** @returns the content area of the given <iframe> or <frame>. */
export function frameContentArea(frame: FrameLikeElement): Rect {
  if (!frame || !frame.ownerDocument || !frame.ownerDocument.defaultView) {
    return new Rect();
  }

  const frameBorderBoxBounds = frame.getBoundingClientRect();
  if (frameBorderBoxBounds.x === undefined || frameBorderBoxBounds.y === undefined) {
    // JSDOM can return an incompletely initialized rect; just bail in that case.
    return new Rect();
  }

  // This calculation is a bit more awkward than it should be, because
  // getBoundingClientRect() includes the border and padding of the iframe; we
  // need to remove them to get *only* the content area. This area corresponds
  // to the viewport inside of the frame.
  const frameStyle = frame.ownerDocument.defaultView.getComputedStyle(frame);
  const insetLeft =
    parseFloat(frameStyle.borderLeftWidth || '0') + parseFloat(frameStyle.paddingLeft || '0');
  const insetRight =
    parseFloat(frameStyle.borderRightWidth || '0') + parseFloat(frameStyle.paddingRight || '0');
  const insetTop =
    parseFloat(frameStyle.borderTopWidth || '0') + parseFloat(frameStyle.paddingTop || '0');
  const insetBottom =
    parseFloat(frameStyle.borderBottomWidth || '0') + parseFloat(frameStyle.paddingBottom || '0');
  return insetBy(frameBorderBoxBounds, insetLeft, insetTop, insetRight, insetBottom);
}

/**
 * @returns the transformation matrix that maps points from the coordinate system
 * of the owner document of the given frame to the coordinate system of the frame's
 * inner document.
 *
 * This transform can be used to map clicks or mouse movements received by an
 * event handler in the outer document into the document inside the frame. To do
 * the reverse, use the inverse of the returned matrix.
 */
export function transformFromOuterDocToFrameDoc(frame: FrameLikeElement): Matrix {
  const matrix = new Matrix();

  const innerDoc = frame.contentDocument;
  const innerDocElem = innerDoc?.documentElement;
  const innerWindow = frame.contentWindow;
  if (!innerDoc || !innerDocElem || !innerWindow) {
    return new Matrix();
  }

  // Compute the bounds of the viewport inside the frame relative to the frame's
  // inner document. getBoundingClientRect() mostly does what we need here, but
  // it can return a rect smaller than the viewport if the document doesn't
  // contain much content, so we also need to check innerWidth/Height.
  const innerDocBounds = innerDocElem.getBoundingClientRect();
  const innerViewportBounds = Rect.fromRect({
    x: innerDocBounds.x,
    y: innerDocBounds.y,
    width: Math.max(innerDocBounds.width, innerWindow.innerWidth),
    height: Math.max(innerDocBounds.height, innerWindow.innerHeight),
  });
  if (innerViewportBounds.width === 0 || innerViewportBounds.height === 0) {
    // The viewport has zero size; there's no useful way to map a point from the
    // frame's owner document into it.
    return matrix;
  }

  // Compute the bounds of the content area of the frame; this is the area in
  // the outer document corresponding to the viewport inside the frame.
  const frameContentBoxBounds = frameContentArea(frame);
  if (
    frameContentBoxBounds.width === 0 ||
    frameContentBoxBounds.height === 0 ||
    isNaN(frameContentBoxBounds.width) ||
    isNaN(frameContentBoxBounds.height) ||
    isNaN(frameContentBoxBounds.x) ||
    isNaN(frameContentBoxBounds.y)
  ) {
    // The frame's content area has zero size; there's no useful way to map a
    // point from the frame's owner document into the inner document.
    return matrix;
  }

  const scale = innerViewportBounds.width / frameContentBoxBounds.width;
  if (frameContentBoxBounds.x === 0 && frameContentBoxBounds.y === 0 && scale === 1) {
    // The transform is just the identity matrix; we can skip the rest of the
    // computations.
    return matrix;
  }

  // We now know how to transform a point in the outer frame's owner document to
  // the equivalent point in the inner document. We can calculate this transform
  // because we know the bounds of the inner document's viewport in both
  // coordinate systems: innerViewportBounds gives us the bounds from the inner
  // document's perspective, and frameContentBoxBounds gives us the bounds from
  // the perspective of the outer frame's owner document.
  matrix.scaleSelf(scale);
  matrix.translateSelf(-frameContentBoxBounds.x, -frameContentBoxBounds.y);

  return matrix;
}
